/**
 * Helpers for the read-only Revision History view.
 *
 * The backend (`GET /api/v2/revisions/history`) already sorts `versions` ascending
 * and each version's `timeline` in display order, so nothing here re-sorts a
 * timeline - it only turns the raw entries into the labels the UI shows.
 *
 * Phase 1 of the API leaves `actor` null on several actions and `at` null on
 * `accepted` / `sent_for_approval`. Both are expected, not errors: the label
 * simply drops the "by whom" part and no time is rendered.
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * ISO 8601 string -> "Sep 24, 2026, 3:04 PM". Returns '' for null/invalid input
 * so callers can render nothing instead of "Invalid Date".
 */
export const formatHistoryDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const hours24 = date.getHours();
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = `${date.getMinutes()}`.padStart(2, '0');

  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${hours12}:${minutes} ${meridiem}`;
};

/** ISO 8601 string -> "Sep 24, 2026". Returns '' for null/invalid input. */
export const formatHistoryDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

/** "sent_for_approval" -> "Sent for approval", for actions added after this build. */
const humaniseAction = (action) => {
  const words = `${action || ''}`.replace(/_/g, ' ').trim();
  if (!words) return 'Activity';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Label for one timeline entry. `version` is the version number the entry
 * belongs to, used only by the `delivered` action.
 *
 * Unrecognised actions fall through to humanised text so a new backend action
 * renders as readable text rather than breaking the list.
 */
export const formatTimelineLabel = (entry, version) => {
  if (!entry) return '';
  const actorName = entry?.actor?.name;
  const by = actorName ? ` by ${actorName}` : '';

  switch (entry.action) {
    case 'delivered':
      return `Version ${version} delivered${by}`;
    case 'revision_started':
      return `Revision requested${by}`;
    case 'note_added':
      return actorName ? `${actorName} added a note` : 'Note added';
    case 'submitted':
      return `Revision submitted${by}`;
    case 'sent_for_approval':
      return `Sent for approval${by}`;
    case 'accepted':
      return `Accepted${by}`;
    default:
      return `${humaniseAction(entry.action)}${by}`;
  }
};

/**
 * A note counts as edited when someone other than the author touched it, or when
 * `updated_at` moved past `created_at`.
 */
export const isNoteEdited = (note) => {
  if (!note) return false;
  const authorId = note?.author?.id;
  const editorId = note?.updated_by?.id;
  const authorType = note?.author?.type;
  const editorType = note?.updated_by?.type;

  if (note?.updated_by && (authorId !== editorId || authorType !== editorType)) return true;

  if (!note?.created_at || !note?.updated_at) return false;
  const created = new Date(note.created_at).getTime();
  const updated = new Date(note.updated_at).getTime();
  if (isNaN(created) || isNaN(updated)) return false;
  return updated > created;
};

/**
 * Newest version first for the list. The API sends ascending order; this returns
 * a reversed copy so the source array is never mutated.
 */
export const sortVersionsForDisplay = (versions) => {
  if (!Array.isArray(versions)) return [];
  return [...versions].reverse();
};

/**
 * Message shown when the history call fails. 401 never reaches here - the shared
 * `showSnackBar` helper logs the user out on 401 before this is consulted.
 */
export const revisionHistoryErrorMessage = (httpCode, message) => {
  switch (httpCode) {
    case 403:
      return "You don't have access to revision history";
    case 404:
      return 'Project or version not found';
    default:
      return message || 'Something went wrong while loading revision history';
  }
};
